import React, { useState, useEffect } from "react";
import { Link } from "@quintype/components";
import DownArrow from "../../../../../atoms/other-icons/DownArrow";
import { AND_MORE_TEXT } from "../../../../../../constants";
import "./hamburger.m.css";

export default function DesktopHamburgerMenuItem({
  item,
  index,
  isActive,
  toggleMenu,
  currentPath,
  allMenus,
  currentIndex,
  menuType,
  primaryMenuCount,
}) {
  const children = item.children ?? [];
  const hasChildren = children.length > 0;

  const childSummary = children
    .slice(0, 2)
    .map((c) => c.title)
    .join(", ");

  const moreText = children.length > 2 ? ` ${AND_MORE_TEXT}` : "";

  // State to track if we're on the client (hydrated)
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Helper to extract path from URL (handles both full URLs and relative paths)
  const extractPath = (url) => {
    if (!url) return '';
    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        // Ignore external domains to prevent false positive highlights
        if (typeof window !== 'undefined' && urlObj.hostname !== window.location.hostname) {
          return url; // Returning full URL means it won't match the current path
        }
        return urlObj.pathname;
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  // Check if current path matches this menu or its children
  const checkMatch = (menu, path) => {
    if (!path) return false;
    const cleanPath = path.split('?')[0].split('#')[0];

    const menuPath = extractPath(menu.completeUrl);
    if (menuPath === cleanPath) return true;
    if (cleanPath.startsWith(menuPath + '/')) return true;

    if (menu.children && menu.children.length > 0) {
      for (const child of menu.children) {
        const itemType = child['item-type'] || child.itemType;
        if (itemType === 'placeholder') {
          continue;
        }

        const childUrl = child.completeUrl || child.url;
        const childPath = extractPath(childUrl);

        if (childPath === cleanPath) return true;
        if (cleanPath.startsWith(childPath + '/')) return true;

        if (child.children && child.children.length > 0) {
          for (const grandchild of child.children) {
            const grandchildItemType = grandchild['item-type'] || grandchild.itemType;
            if (grandchildItemType === 'placeholder') {
              continue;
            }

            const grandchildUrl = grandchild.completeUrl || grandchild.url;
            const grandchildPath = extractPath(grandchildUrl);
            if (grandchildPath === cleanPath) return true;
            if (cleanPath.startsWith(grandchildPath + '/')) return true;
          }
        }
      }
    }
    return false;
  };

  // Determine if this menu should be highlighted
  const highlighted = (() => {
    if (!isClient) return false;
    if (!currentPath) return false;

    // Skip placeholder items
    const itemType = item['item-type'] || item.itemType;
    if (itemType === 'placeholder') {
      return false;
    }

    const cleanPath = currentPath.split('?')[0].split('#')[0];
    const matches = checkMatch(item, currentPath);

    if (!matches) return false;

    if (isClient) {
      try {
        const clicked = sessionStorage.getItem('clickedMenu');
        if (clicked) {
          const { url, menuId, menuTitle, type, index: clickedIndex } = JSON.parse(clicked);
          if (url === cleanPath) {
            // If types don't match, don't highlight this menu
            if (type !== menuType) {
              return false;
            }
            // Prefer matching by index, then ID, then title
            if (clickedIndex !== undefined && currentIndex !== undefined) {
              return currentIndex === clickedIndex;
            }
            if (menuId && item.id) {
              return item.id === menuId;
            }
            return item.title === menuTitle;
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Only check within same menu type for first matching menu
    if (allMenus && currentIndex !== undefined && primaryMenuCount !== undefined) {
      for (let i = 0; i < currentIndex; i++) {
        // Determine the type of the other menu based on its index
        const otherMenuType = i < primaryMenuCount ? 'primary' : 'secondary';
        // Only compare with menus of the same type
        if (otherMenuType === menuType && checkMatch(allMenus[i], currentPath)) {
          return false;
        }
      }
    }

    return true;
  })();

  // Store which parent menu was clicked
  const handleMenuClick = () => {
    try {
      const urlPath = extractPath(item.completeUrl);
      const cleanUrl = urlPath.split('?')[0].split('#')[0];
      sessionStorage.setItem('clickedMenu', JSON.stringify({
        url: cleanUrl,
        menuTitle: item.title,
        menuId: item.id,
        type: menuType,
        index: currentIndex
      }));
    } catch (e) {
      // Ignore storage errors
    }
  };

  return (
    <div styleName="menu-column">
      <div styleName={`menu-card ${isActive ? "active" : ""} ${highlighted ? "highlighted" : ""}`}>
        <div styleName="menu-card-header">
          <Link href={item.completeUrl} styleName="menu-title" onMouseDown={handleMenuClick}>
            <div styleName="parent-title-text">{item.title}</div>
            {hasChildren && (
              <div styleName="menu-child-summary">
                {childSummary}
                {moreText}
              </div>
            )}
          </Link>

          {hasChildren && (
            <span styleName={`arrow ${isActive ? "arrow-rotated" : ""}`} onClick={() => toggleMenu(index)}>
              <DownArrow color={isActive || highlighted ? "#B82831" : "#666"} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
